/*
 * <oilogic-collection-filters> — Oilogic Care
 * AJAX filtering/sorting via the Section Rendering API: sort dropdown,
 * drawer data (chips, clear-all, counts) and URL sync. Drawer open/close
 * lives in <oilogic-filter-drawer>. Infinite scroll appends pages via a
 * sentinel observer. Without JS the GET form submits normally.
 */
class OilogicCollectionFilters extends HTMLElement {
  connectedCallback() {
    this.controller = new AbortController();
    const { signal } = this.controller;

    this.form = this.querySelector('form');
    this.results = document.getElementById(`CollectionResults-${this.dataset.sectionId}`);
    if (!this.form || !this.results) return;

    // On the host, not the form: the drawer checkboxes sit outside the
    // <form> (form-attribute associated) and their changes bubble here.
    this.addEventListener(
      'change',
      (event) => {
        // Sorting is a single choice: reflect it on the pill and close the
        // dropdown right away (filter checkboxes stay open for multi-select).
        if (event.target.name === 'sort_by') {
          this.updateSortLabel();
          const facet = event.target.closest('details[data-facet]');
          if (facet) facet.open = false;
        }
        this.applyFilters();
      },
      { signal }
    );
    this.form.addEventListener(
      'submit',
      (event) => {
        event.preventDefault();
        // Drawer Show button: filters already applied on change — the drawer
        // element closes itself, nothing left to fetch.
        if (event.submitter && event.submitter.hasAttribute('data-drawer-show')) return;
        this.applyFilters();
      },
      { signal }
    );

    this.addEventListener(
      'click',
      (event) => {
        const removeChip = event.target.closest('[data-drawer-remove]');
        if (removeChip) {
          event.preventDefault();
          const url = new URL(removeChip.href);
          this.syncInputs(url.searchParams);
          this.renderFromUrl(url, true);
          return;
        }

        const clear = event.target.closest('[data-drawer-clear]');
        if (!clear) return;
        event.preventDefault();
        this.querySelectorAll('[data-drawer-body] input[type="checkbox"]:checked').forEach((input) => {
          input.checked = false;
        });
        this.applyFilters();
      },
      { signal }
    );

    // Only one dropdown open at a time (`toggle` does not bubble — capture).
    this.addEventListener('toggle', (event) => this.onToggle(event), { signal, capture: true });
    document.addEventListener('click', (event) => this.onDocumentClick(event), { signal });
    this.addEventListener(
      'keydown',
      (event) => {
        if (event.key === 'Escape') this.closeAll(true);
      },
      { signal }
    );

    // Paginate without a full reload once results have been swapped in.
    this.results.addEventListener(
      'click',
      (event) => {
        const pageLink = event.target.closest('.main-collection__pagination a[href]');
        if (pageLink) {
          event.preventDefault();
          this.renderFromUrl(new URL(pageLink.href), true);
          return;
        }

        // "Load more" is a real link (works without JS); a click behaves
        // the same as the IntersectionObserver auto-trigger.
        const loadMoreLink = event.target.closest('.main-collection__load-more');
        if (!loadMoreLink) return;
        event.preventDefault();
        this.loadNextPage();
      },
      { signal }
    );

    window.addEventListener(
      'popstate',
      () => {
        const url = new URL(window.location.href);
        this.syncInputs(url.searchParams);
        this.updateSortLabel();
        this.renderFromUrl(url, false);
      },
      { signal }
    );

    this.setupInfiniteScroll();
  }

  disconnectedCallback() {
    if (this.controller) this.controller.abort();
    if (this.fetchController) this.fetchController.abort();
    if (this.scrollObserver) this.scrollObserver.disconnect();
  }

  setupInfiniteScroll() {
    if (this.scrollObserver) this.scrollObserver.disconnect();
    const sentinel = this.results.querySelector('[data-infinite-scroll-sentinel]');
    if (!sentinel) return;
    this.scrollObserver = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) this.loadNextPage();
      },
      { rootMargin: '400px' }
    );
    this.scrollObserver.observe(sentinel);
  }

  async loadNextPage() {
    const sentinel = this.results.querySelector('[data-infinite-scroll-sentinel]');
    if (!sentinel || this.loadingNextPage) return;
    const nextUrl = sentinel.dataset.nextUrl;
    if (!nextUrl) return;

    this.loadingNextPage = true;
    this.scrollObserver.unobserve(sentinel);
    sentinel.setAttribute('aria-busy', 'true');
    const link = sentinel.querySelector('.main-collection__load-more');
    const status = sentinel.querySelector('.main-collection__loading');
    if (link) link.hidden = true;
    if (status) status.hidden = false;

    const sectionUrl = new URL(nextUrl, window.location.origin);
    sectionUrl.searchParams.set('section_id', this.dataset.sectionId);

    try {
      const response = await fetch(sectionUrl);
      if (!response.ok) throw new Error('infinite scroll fetch failed');
      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');

      const newGrid = doc.querySelector('.main-collection__grid');
      const grid = this.results.querySelector('.main-collection__grid');
      if (newGrid && grid) grid.append(...newGrid.children);

      const newSentinel = doc.querySelector('[data-infinite-scroll-sentinel]');
      if (newSentinel && newSentinel.dataset.nextUrl) {
        sentinel.dataset.nextUrl = newSentinel.dataset.nextUrl;
        if (link) link.href = newSentinel.dataset.nextUrl;
        sentinel.removeAttribute('aria-busy');
        if (link) link.hidden = false;
        if (status) status.hidden = true;
        this.scrollObserver.observe(sentinel);
      } else {
        sentinel.remove();
      }

      const newCount = doc.querySelector('[data-products-count]');
      const count = this.querySelector('[data-products-count]');
      if (newCount && count) count.textContent = newCount.textContent;

      window.history.replaceState({}, '', nextUrl);
      document.dispatchEvent(new CustomEvent('oilogic:collection:updated', { bubbles: true }));
    } catch (error) {
      sentinel.removeAttribute('aria-busy');
      if (link) link.hidden = false;
      if (status) status.hidden = true;
      this.scrollObserver.observe(sentinel);
    } finally {
      this.loadingNextPage = false;
    }
  }

  onToggle(event) {
    const opened = event.target;
    if (!(opened instanceof HTMLElement) || !opened.hasAttribute('data-facet') || !opened.open) return;
    this.querySelectorAll('details[data-facet][open]').forEach((facet) => {
      if (facet !== opened) facet.open = false;
    });
  }

  onDocumentClick(event) {
    // Close open dropdowns on any click outside a facet (the host wraps
    // the results too, so "outside the host" is not enough).
    if (!event.target.closest('details[data-facet]')) this.closeAll(false);
  }

  closeAll(refocus) {
    this.querySelectorAll('details[data-facet][open]').forEach((facet) => {
      facet.open = false;
      if (refocus) facet.querySelector('summary').focus();
    });
  }

  applyFilters() {
    const url = new URL(this.form.action, window.location.origin);
    url.search = new URLSearchParams(new FormData(this.form)).toString();
    this.renderFromUrl(url, true);
  }

  async renderFromUrl(url, pushHistory) {
    if (this.fetchController) this.fetchController.abort();
    this.fetchController = new AbortController();
    this.results.setAttribute('aria-busy', 'true');

    const sectionUrl = new URL(url);
    sectionUrl.searchParams.set('section_id', this.dataset.sectionId);

    try {
      const response = await fetch(sectionUrl, { signal: this.fetchController.signal });
      if (!response.ok) throw new Error('collection filter fetch failed');
      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');

      // If focus lived inside the results (e.g. a pagination link), the swap
      // destroys it — restore focus to the results container afterwards.
      const hadFocus = this.results.contains(document.activeElement);
      const newResults = doc.getElementById(`CollectionResults-${this.dataset.sectionId}`);
      if (newResults) this.results.innerHTML = newResults.innerHTML;
      if (hadFocus) this.results.focus({ preventScroll: true });

      const newCount = doc.querySelector('[data-products-count]');
      const count = this.querySelector('[data-products-count]');
      if (newCount && count) count.textContent = newCount.textContent;

      this.updateDrawer(doc);
      this.setupInfiniteScroll();
      if (pushHistory) window.history.pushState({}, '', url);
      this.results.removeAttribute('aria-busy');
      document.dispatchEvent(new CustomEvent('oilogic:collection:updated', { bubbles: true }));
    } catch (error) {
      if (error.name === 'AbortError') return;
      // Fall back to full navigation so filtering still works.
      window.location.assign(url);
    }
  }

  updateSortLabel() {
    const label = this.querySelector('[data-sort-label]');
    const checked = this.form.querySelector('input[name="sort_by"]:checked');
    if (!label || !checked) return;
    const option = checked.closest('.facet__option')?.querySelector('.facet__option-label');
    if (option) label.textContent = option.textContent;
  }

  syncInputs(params) {
    // Host-wide: sort radios (in the form) + drawer checkboxes (outside).
    this.querySelectorAll('input[type="checkbox"], input[type="radio"]').forEach((input) => {
      input.checked = params.getAll(input.name).includes(input.value);
    });
  }

  // Re-render the drawer from the fetched section (chips, counts, value
  // availability); accordion states, scroll and focus survive the swap.
  updateDrawer(doc) {
    const drawer = this.querySelector('[data-filter-drawer]');
    const newDrawer = doc.querySelector('[data-filter-drawer]');
    if (!drawer || !newDrawer) return;

    const chip = drawer.querySelector('[data-drawer-chip]');
    const newChip = newDrawer.querySelector('[data-drawer-chip]');
    if (chip && newChip) {
      chip.textContent = newChip.textContent;
      chip.hidden = newChip.hidden;
    }

    const show = drawer.querySelector('[data-drawer-show]');
    const newShow = newDrawer.querySelector('[data-drawer-show]');
    if (show && newShow) show.textContent = newShow.textContent;

    const body = drawer.querySelector('[data-drawer-body]');
    const newBody = newDrawer.querySelector('[data-drawer-body]');
    if (!body || !newBody) return;

    const closed = [];
    body.querySelectorAll('details[data-filter-group]:not([open])').forEach((group) => {
      closed.push(group.dataset.filterGroup);
    });
    const scroller = drawer.querySelector('[data-drawer-scroll]');
    const scrollTop = scroller ? scroller.scrollTop : 0;
    const active = document.activeElement;
    const refocus =
      body.contains(active) && active.matches('.facet__input')
        ? { name: active.name, value: active.value }
        : null;

    body.innerHTML = newBody.innerHTML;

    closed.forEach((param) => {
      const group = body.querySelector(`details[data-filter-group="${CSS.escape(param)}"]`);
      if (group) group.open = false;
    });
    if (scroller) scroller.scrollTop = scrollTop;
    if (refocus) {
      const input = Array.from(body.querySelectorAll('.facet__input')).find(
        (el) => el.name === refocus.name && el.value === refocus.value
      );
      if (input) input.focus({ preventScroll: true });
    }
  }
}

customElements.define('oilogic-collection-filters', OilogicCollectionFilters);
